import { useCallback, useEffect, useMemo } from 'react';

import { useChannelAdminContext } from 'hooks';

import { useProductsListQuery, useProductsListForExport } from '../../helpers';
import { setChargeCodes, setProductsSettings } from '../helpers/productsTable';
import { useTableControls } from './useTableControls';
import { SortDirection } from 'graphql-query-contracts';

const DEFAULT_SORT = {
  id: 'name',
  direction: SortDirection.Asc,
};

export const useProductServiceTable = () => {
  const { channel } = useChannelAdminContext();
  const { products, pageInfo, getProducts, isLoading, error } =
    useProductsListQuery();

  const { getProductsForExport } = useProductsListForExport();
  const {
    page,
    searchKey,
    filters,
    sortBy,
    setPage,
    setSearchKey,
    setFilters,
    setSortBy,
  } = useTableControls();

  const fetchProducts = useCallback(async () => {
    if (!channel?._id) {
      return;
    }

    const productsSettings = setProductsSettings(filters);
    const chargeCodes = setChargeCodes(filters);

    await getProducts(channel._id, channel?.parent?._id, productsSettings, {
      page: page.index,
      pageSize: page.size,
      sort: sortBy,
      filters: {
        name: searchKey,
        chargeCodes,
      },
    });
  }, [channel?._id, filters, page.index, page.size, sortBy, searchKey]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getDefaultProductsForCurrentPage = useCallback(async () => {
    if (!channel?._id) {
      return;
    }

    const productsSettings = setProductsSettings(filters);

    await getProducts(channel._id, channel?.parent?._id, productsSettings, {
      page: page.index,
      pageSize: page.size,
      sort: DEFAULT_SORT,
    });
  }, [channel?._id, filters, page.index, page.size]);

  const getBulkProductsAndServices = useCallback(async () => {
    if (!channel?._id) {
      return;
    }

    const productsSettings = setProductsSettings(filters);
    const chargeCodes = setChargeCodes(filters);

    return await getProductsForExport(
      channel._id,
      channel?.parent?._id,
      productsSettings,
      {
        sort: DEFAULT_SORT,
        filters: {
          name: searchKey,
          chargeCodes,
        },
      }
    );
  }, [channel?._id, filters, sortBy, searchKey]);

  return useMemo(
    () => ({
      channel: {
        id: channel?._id,
        parentId: channel?.parent?._id,
      },
      page: {
        index: page.index,
        size: page.size,
        total: pageInfo?.total,
      },
      products: {
        items: products,
        isLoading,
        error,
      },
      refetchProducts: getDefaultProductsForCurrentPage,
      getBulkProductsAndServices,
      setPage,
      setSearchKey,
      setFilters,
      setSortBy,
    }),
    [
      channel?._id,
      channel?.parent?._id,
      page,
      pageInfo?.total,
      products,
      isLoading,
      error,
      getDefaultProductsForCurrentPage,
      getBulkProductsAndServices,
      setPage,
      setSearchKey,
      setFilters,
      setSortBy,
    ]
  );
};
